import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../hooks/useAuth'
import { supportService } from '../../services/supportService'
import {
  SupportTicketDetail,
  SupportTicketStatus,
  SupportTicketPriority,
  SupportMessage,
} from '../../types/support'
import { Button } from '../../components/ui/Button'
import { PageEyebrow, PageTitle, PageHeader, PageMeta } from '../../components/ui/Table'

// ─── Styles ───────────────────────────────────────────────
const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 20px;
  align-items: start;
`

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2d9f3;
  padding: 20px;
`

const CardTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: #3d2460;
  margin-bottom: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const BodyText = styled.p`
  font-size: 14px;
  color: #3d1a0a;
  line-height: 1.7;
  white-space: pre-wrap;
`

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  max-height: 440px;
  overflow-y: auto;
`

interface $Support {
  $fromSupport: boolean
  $isInternal: boolean
}

const Bubble = styled.div<$Support>`
  padding: 10px 14px;
  border-radius: 12px;
  max-width: 90%;
  align-self: ${({ $fromSupport }) => ($fromSupport ? 'flex-end' : 'flex-start')};
  background: ${({ $fromSupport, $isInternal }) => {
    if ($isInternal) return '#fef3c7'
    return $fromSupport ? '#ede9fe' : '#f0fdf4'
  }};
  border: 1px solid
    ${({ $fromSupport, $isInternal }) => {
      if ($isInternal) return '#fde68a'
      return $fromSupport ? '#c4b5fd' : '#bbf7d0'
    }};
`

const BubbleMeta = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`

const BubbleBody = styled.p`
  font-size: 13px;
  color: #1e293b;
  line-height: 1.5;
  white-space: pre-wrap;
`

const InternalTag = styled.span`
  font-size: 10px;
  background: #fde68a;
  color: #92400e;
  padding: 1px 6px;
  border-radius: 8px;
`

const ReplyBox = styled.div`
  border-top: 1px solid #e2d9f3;
  padding-top: 14px;
`

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e2d9f3;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  outline: none;
  color: #3d1a0a;

  &:focus {
    border-color: #9b6bb5;
  }
`

const ReplyActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
`

const Field = styled.div`
  margin-bottom: 12px;
`

const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
`

const FieldSelect = styled.select`
  width: 100%;
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid #e2d9f3;
  font-size: 13px;
  color: #3d1a0a;
  outline: none;
  background: white;

  &:focus {
    border-color: #9b6bb5;
  }
`

const MetaItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f8f4ff;
  font-size: 12px;
  color: #64748b;

  strong {
    color: #3d2460;
    font-weight: 500;
  }
`

const LinearSection = styled.div`
  margin-top: 6px;
`

const LinearLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #5e6ad2;
  font-size: 12px;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`

const ErrorMsg = styled.p`
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
`

const BackButton = styled.button`
  background: none;
  border: none;
  color: #9b6bb5;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #6b21a8;
  }
`

const MERCURE_URL = `${process.env.REACT_APP_API_URL ?? 'https://localhost:8443'}/.well-known/mercure`

// ─── Component ────────────────────────────────────────────
export function SupportDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { token } = useAuth()
  const svc = useMemo(() => supportService(token), [token])

  const [ticket, setTicket] = useState<SupportTicketDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [replyBody, setReplyBody] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [replying, setReplying] = useState(false)
  const [escalating, setEscalating] = useState(false)
  const [linearError, setLinearError] = useState('')
  const threadRef = useRef<HTMLDivElement>(null)
  const seenMessageIds = useRef<Set<string>>(new Set())

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const data = await svc.show(id)
      seenMessageIds.current = new Set(data.messages.map(m => m.id))
      setTicket(data)
    } finally {
      setLoading(false)
    }
  }, [id, svc])

  useEffect(() => {
    load()
  }, [load])

  // Real-time message delivery via Mercure
  useEffect(() => {
    if (!id) return undefined
    const url = new URL(MERCURE_URL)
    url.searchParams.append('match', `support/ticket/${id}`)
    const es = new EventSource(url.toString())
    es.onmessage = e => {
      try {
        const { message } = JSON.parse(e.data) as { message: SupportMessage }
        if (seenMessageIds.current.has(message.id)) return
        seenMessageIds.current.add(message.id)
        setTicket(prev => (prev ? { ...prev, messages: [...prev.messages, message] } : prev))
      } catch {
        /* malformed */
      }
    }
    return () => es.close()
  }, [id])

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight
    }
  }, [ticket?.messages.length])

  const handleStatusChange = useCallback(
    async (status: SupportTicketStatus) => {
      if (!ticket) return
      const updated = await svc.update(ticket.id, { status })
      setTicket(updated)
    },
    [ticket, svc]
  )

  const handlePriorityChange = useCallback(
    async (priority: SupportTicketPriority) => {
      if (!ticket) return
      const updated = await svc.update(ticket.id, { priority })
      setTicket(updated)
    },
    [ticket, svc]
  )

  const onReplyChange = useRef((v: string) => setReplyBody(v))

  const handleReply = useCallback(async () => {
    if (!ticket || !replyBody.trim()) return
    setReplying(true)
    try {
      await svc.addMessage(ticket.id, { body: replyBody.trim(), isInternal })
      setReplyBody('')
      setIsInternal(false)
    } finally {
      setReplying(false)
    }
  }, [ticket, replyBody, isInternal, svc])

  const handleEscalate = useCallback(async () => {
    if (!ticket) return
    setEscalating(true)
    setLinearError('')
    try {
      const result = await svc.escalate(ticket.id)
      setTicket(prev =>
        prev
          ? {
              ...prev,
              isEscalated: true,
              linearIssueUrl: result.linearIssueUrl,
            }
          : prev
      )
    } catch (err: unknown) {
      const e = err as { message?: string }
      setLinearError(e?.message ?? 'Failed to create Linear issue')
    } finally {
      setEscalating(false)
    }
  }, [ticket, svc])

  if (loading) return <p style={{ padding: 24, color: '#94a3b8' }}>Loading…</p>
  if (!ticket) return <p style={{ padding: 24, color: '#ef4444' }}>Ticket not found</p>

  return (
    <>
      <PageHeader>
        <PageMeta>
          <BackButton onClick={() => navigate('/support')}>← Back to tickets</BackButton>
          <PageEyebrow>Support / {ticket.type}</PageEyebrow>
          <PageTitle>{ticket.subject}</PageTitle>
        </PageMeta>
      </PageHeader>

      <Layout>
        {/* ── Main column ── */}
        <div>
          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Description</CardTitle>
            <BodyText>{ticket.body}</BodyText>
          </Card>

          <Card>
            <CardTitle>Thread</CardTitle>
            <Thread ref={threadRef}>
              {ticket.messages.length === 0 && (
                <p style={{ fontSize: 13, color: '#94a3b8' }}>No replies yet.</p>
              )}
              {ticket.messages.map(msg => (
                <Bubble key={msg.id} $fromSupport={msg.fromSupport} $isInternal={msg.isInternal}>
                  <BubbleMeta>
                    <span>{msg.sender?.name ?? 'User'}</span>
                    {msg.isInternal && <InternalTag>internal</InternalTag>}
                    <span style={{ marginLeft: 'auto' }}>
                      {new Date(msg.createdAt).toLocaleString()}
                    </span>
                  </BubbleMeta>
                  <BubbleBody>{msg.body}</BubbleBody>
                </Bubble>
              ))}
            </Thread>

            <ReplyBox>
              <Textarea
                placeholder="Write a reply…"
                value={replyBody}
                onChange={e => onReplyChange.current(e.target.value)}
              />
              <ReplyActions>
                <Button onClick={handleReply} disabled={replying || !replyBody.trim()}>
                  {replying ? 'Sending…' : 'Send reply'}
                </Button>
                <CheckLabel>
                  <input
                    type="checkbox"
                    checked={isInternal}
                    onChange={e => setIsInternal(e.target.checked)}
                  />
                  Internal note
                </CheckLabel>
              </ReplyActions>
            </ReplyBox>
          </Card>
        </div>

        {/* ── Sidebar column ── */}
        <div>
          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Details</CardTitle>

            <Field>
              <FieldLabel>Status</FieldLabel>
              <FieldSelect
                value={ticket.status}
                onChange={e => handleStatusChange(e.target.value as SupportTicketStatus)}
              >
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </FieldSelect>
            </Field>

            <Field>
              <FieldLabel>Priority</FieldLabel>
              <FieldSelect
                value={ticket.priority}
                onChange={e => handlePriorityChange(e.target.value as SupportTicketPriority)}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </FieldSelect>
            </Field>

            <MetaItem>
              <span>Platform</span>
              <strong>{ticket.platform}</strong>
            </MetaItem>
            {ticket.appVersion && (
              <MetaItem>
                <span>App version</span>
                <strong>{ticket.appVersion}</strong>
              </MetaItem>
            )}
            <MetaItem>
              <span>Created</span>
              <strong>{new Date(ticket.createdAt).toLocaleDateString()}</strong>
            </MetaItem>
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Reporter</CardTitle>
            {ticket.user ? (
              <>
                <MetaItem>
                  <span>Name</span>
                  <strong>{ticket.user.name}</strong>
                </MetaItem>
                <MetaItem>
                  <span>Email</span>
                  <strong style={{ fontSize: 11 }}>{ticket.user.email}</strong>
                </MetaItem>
              </>
            ) : (
              <p style={{ fontSize: 13, color: '#94a3b8' }}>Anonymous</p>
            )}
          </Card>

          <Card>
            <CardTitle>Linear</CardTitle>
            {ticket.isEscalated && ticket.linearIssueUrl ? (
              <LinearSection>
                <LinearLink href={ticket.linearIssueUrl} target="_blank" rel="noopener noreferrer">
                  ↗ View issue in Linear
                </LinearLink>
              </LinearSection>
            ) : (
              <LinearSection>
                <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
                  Create a Linear issue for the dev team to track this ticket.
                </p>
                <Button onClick={handleEscalate} disabled={escalating}>
                  {escalating ? 'Creating…' : '↗ Escalate to Linear'}
                </Button>
                {linearError && <ErrorMsg>{linearError}</ErrorMsg>}
              </LinearSection>
            )}
          </Card>
        </div>
      </Layout>
    </>
  )
}
